import { useState } from 'react';
import { NutzerChip } from '@/components/ui/NutzerChip';
import { Phone, Mail, Store, MailOpen, Voicemail, StickyNote, Pencil, Info } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { datumZeitDe, relativZeitDe } from '@/lib/format';
import type { DetailKommunikation } from '@/types/detail';

const ICONS: Record<string, typeof Phone> = {
  phone: Phone,
  mail: Mail,
  store: Store,
  'mail-open': MailOpen,
  voicemail: Voicemail,
  'sticky-note': StickyNote
};

const ERG_LABEL: Record<string, string> = {
  erreicht: 'erreicht',
  mailbox: 'Mailbox',
  kein_anschluss: 'kein Anschluss',
  nicht_erreicht: 'nicht erreicht',
  keine_antwort: 'keine Antwort'
};

function minutenBis(iso: string): number {
  const d = new Date(iso.replace(' ', 'T') + 'Z').getTime();
  return Math.max(0, Math.round((d - Date.now()) / 60_000));
}

type Props = {
  eintrag: DetailKommunikation;
  onUpdate: (id: number, freitext: string) => void;
};

export function KommunikationEintrag({ eintrag: e, onUpdate }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(e.freitext ?? '');
  const Icon = (e.typ.icon && ICONS[e.typ.icon]) || StickyNote;
  const editierbar = e.editierbar_bis !== null;
  const restMin = editierbar ? minutenBis(e.editierbar_bis!) : 0;
  // Leerer Entwurf sperrt Speichern — aber nicht mehr wortlos. Das Backend
  // weist ein Leeren mit 400 ab (append-only, SPEC-011); ein Knopf, der
  // ohne Begruendung grau ist, sieht dagegen nach Fehler aus.
  const entwurfLeer = draft.trim() === '';
  const hinweisId = `komm-leer-${e.id}`;

  return (
    <div className="rounded-md border border-line bg-cream p-3">
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ink-soft" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-ink-faint">
            {/* A9.4: Datum und Uhrzeit stehen jetzt IN der Zeile, nicht
                mehr nur im title-Attribut. Auf dem Tablet gibt es keinen
                Zeiger — dort war die genaue Zeit unerreichbar, obwohl sie
                gespeichert ist.

                Die relative Angabe weicht ganz. Am Tresen zählt „wann
                genau war der Anruf", nicht „vor wie langer Zeit
                ungefähr": Wer zurückruft, sagt „Sie hatten heute um
                10:15 angerufen" — mit „vor 3 Stunden" fängt niemand
                etwas an. Beides nebeneinander wäre dieselbe Auskunft
                zweimal (Designprinzip 4) und in einer ohnehin engen
                Kopfzeile eine Angabe zu viel.

                Der title bleibt: er trägt jetzt die relative Angabe
                nach, für den, der sie doch will. */}
            <span
              className="tabular-nums"
              title={`${relativZeitDe(e.erstellt_am)} erfasst`}
            >
              {datumZeitDe(e.erstellt_am)}
            </span>
            <span>·</span>
            {/* A9.1: Das append-only-Protokoll ist genau die Stelle, an
                der eine Falschzuordnung nicht mehr zu berichtigen ist —
                hier zählt das Erkennungszeichen am meisten. */}
            <span className="inline-flex items-center gap-1 text-ink-soft">
              <NutzerChip
                farbe={e.user.farbe}
                symbol={e.user.symbol}
                kuerzel={e.user.kuerzel}
                name={e.user.name}
                groesse="klein"
              />
              {/* Nur mit Symbol im Chip (A33, nachgezogen in A36.4) —
                  sonst stuenden dieselben Initialen zweimal
                  nebeneinander. */}
              {e.user.symbol !== null && (
                <span className="font-medium">{e.user.kuerzel}</span>
              )}
            </span>
            {e.richtung && (<><span>·</span><span>{e.richtung}</span></>)}
            {e.ergebnis && e.ergebnis !== 'n_a' && (
              <><span>·</span><span>{ERG_LABEL[e.ergebnis] ?? e.ergebnis}</span></>
            )}
            {e.bearbeitet_am && (
              <><span>·</span><span className="italic">bearbeitet</span></>
            )}
          </div>
          {editing ? (
            <div className="mt-2 space-y-2">
              <textarea
                value={draft}
                onChange={(ev) => setDraft(ev.target.value)}
                rows={3}
                className="min-h-[44px] w-full rounded-md border border-line bg-cream px-2 py-1 text-sm"
              />
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  onClick={() => { onUpdate(e.id, draft); setEditing(false); }}
                  disabled={entwurfLeer}
                  aria-describedby={entwurfLeer ? hinweisId : undefined}
                >
                  Speichern
                </Button>
                <Button variant="ghost" onClick={() => { setDraft(e.freitext ?? ''); setEditing(false); }}>
                  Abbrechen
                </Button>
              </div>
              {entwurfLeer && (
                <p id={hinweisId} className="flex items-start gap-1.5 text-xs text-ink-soft">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>Ein Eintrag ist Protokoll: Du kannst ihn korrigieren, aber nicht leeren.</span>
                </p>
              )}
            </div>
          ) : (
            <p className="mt-1 font-serif text-sm italic text-ink">
              „{e.freitext ?? ''}"
            </p>
          )}
          {editierbar && !editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="mt-1 inline-flex items-center gap-1 text-xs text-ink-soft hover:text-akzent"
            >
              <Pencil className="h-3 w-3" />
              Bearbeiten · {restMin} Min möglich
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
